"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.REMOTE_STORAGE_REFUSAL_MESSAGES = exports.parseRemoteStorageUri = exports.formatRemoteStorageUri = exports.isSafeRemotePath = exports.REMOTE_STORAGE_URI_PREFIX = exports.toRcloneOptions = exports.secretProblems = exports.requiredSecretNames = exports.usedSecretNames = exports.normalizeRemoteStorageInput = exports.protocolOf = exports.protocolDefaults = exports.getRemoteStorageTemplate = exports.REMOTE_STORAGE_PROTOCOLS = exports.REMOTE_STORAGE_TEMPLATES = void 0;
const HOST_HELP = "The server's DNS name, for example files.example.org. No scheme, port or path.";
exports.REMOTE_STORAGE_TEMPLATES = {
    sftp: {
        backend: "sftp",
        version: 1,
        pinsHostKey: true,
        variables: [
            {
                name: "host",
                label: "Host",
                kind: "text",
                required: true,
                help: HOST_HELP,
            },
            {
                name: "port",
                label: "Port",
                kind: "port",
                required: true,
                defaultValue: 22,
            },
            { name: "user", label: "User name", kind: "text", required: true },
            {
                name: "auth",
                label: "Sign in with",
                kind: "select",
                required: true,
                defaultValue: "key",
                options: [
                    { value: "key", label: "Private key" },
                    { value: "password", label: "Password" },
                ],
            },
        ],
        secrets: [
            {
                name: "key_pem",
                label: "Private key",
                multiline: true,
                requiredWhen: { variable: "auth", equals: "key" },
            },
            {
                name: "pass",
                label: "Password",
                multiline: false,
                requiredWhen: { variable: "auth", equals: "password" },
            },
        ],
    },
    ftp: {
        backend: "ftp",
        version: 1,
        pinsHostKey: false,
        variables: [
            {
                name: "host",
                label: "Host",
                kind: "text",
                required: true,
                help: HOST_HELP,
            },
            {
                name: "port",
                label: "Port",
                kind: "port",
                required: true,
                defaultValue: 21,
            },
            {
                name: "user",
                label: "User name",
                kind: "text",
                required: true,
                defaultValue: "anonymous",
            },
            {
                name: "tls",
                label: "TLS",
                kind: "select",
                required: true,
                defaultValue: "explicit",
                fromProtocol: true,
                options: [
                    { value: "none", label: "None" },
                    { value: "explicit", label: "Explicit" },
                    { value: "implicit", label: "Implicit" },
                ],
            },
        ],
        secrets: [{ name: "pass", label: "Password", multiline: false }],
    },
};
exports.REMOTE_STORAGE_PROTOCOLS = [
    { value: "sftp", label: "SFTP" },
    { value: "ftp", label: "FTP" },
    { value: "ftps_explicit", label: "FTPS (explicit TLS)" },
    { value: "ftps_implicit", label: "FTPS (implicit TLS)" },
];
const getRemoteStorageTemplate = (backend) => exports.REMOTE_STORAGE_TEMPLATES[backend];
exports.getRemoteStorageTemplate = getRemoteStorageTemplate;
const protocolDefaults = (protocol) => {
    switch (protocol) {
        case "sftp":
            return { backend: "sftp", port: 22 };
        case "ftp":
            return { backend: "ftp", tls: "none", port: 21 };
        case "ftps_explicit":
            return { backend: "ftp", tls: "explicit", port: 21 };
        case "ftps_implicit":
            return { backend: "ftp", tls: "implicit", port: 990 };
    }
};
exports.protocolDefaults = protocolDefaults;
const protocolOf = (backend, variables) => {
    if ("sftp" === backend)
        return "sftp";
    if ("implicit" === variables.tls)
        return "ftps_implicit";
    if ("explicit" === variables.tls)
        return "ftps_explicit";
    return "ftp";
};
exports.protocolOf = protocolOf;
const HOST_NAME = /^(?=.{1,253}$)[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?(\.[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?)*$/;
const IPV4 = /^(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3}$/;
const IPV6 = /^[0-9a-f]{0,4}(:[0-9a-f]{0,4}){2,7}(:(\d{1,3}\.){3}\d{1,3})?$/;
const CONTROL = /[\u0000-\u001f\u007f]/;
const isHostLiteral = (host) => HOST_NAME.test(host) || IPV4.test(host) || IPV6.test(host);
const normalizeRemoteStorageInput = (protocol, raw) => {
    const defaults = (0, exports.protocolDefaults)(protocol);
    const template = exports.REMOTE_STORAGE_TEMPLATES[defaults.backend];
    const problems = [];
    const variables = {};
    for (const field of template.variables) {
        const given = raw[field.name];
        const value = field.fromProtocol
            ? defaults.tls
            : (given !== null && given !== void 0 ? given : ("port" === field.name ? defaults.port : field.defaultValue));
        if (undefined === value || "" === value) {
            if (field.required)
                problems.push(`${field.label} is required.`);
            continue;
        }
        if ("port" === field.kind) {
            const port = Number(value);
            if (!Number.isInteger(port) || port < 1 || port > 65535) {
                problems.push("Port must be a whole number from 1 to 65535.");
                continue;
            }
            variables[field.name] = port;
            continue;
        }
        const text = String(value).trim();
        if ("select" === field.kind &&
            !field.options.some((option) => option.value === text)) {
            problems.push(`${field.label} must be one of ${field.options.map((option) => option.label).join(", ")}.`);
            continue;
        }
        if ("host" === field.name) {
            const host = text
                .toLowerCase()
                .replace(/\.$/, "")
                .replace(/^\[(.*)\]$/, "$1");
            if (!isHostLiteral(host)) {
                problems.push("Host must be a DNS name or an IP address, with no scheme, port or path.");
                continue;
            }
            variables.host = host;
            continue;
        }
        if (CONTROL.test(text) || text.length > 128) {
            problems.push(`${field.label} must be at most 128 characters, with no line breaks.`);
            continue;
        }
        variables[field.name] = text;
    }
    const unknown = Object.keys(raw).filter((key) => !template.variables.some((field) => field.name === key));
    if (unknown.length > 0)
        problems.push(`Unknown settings: ${unknown.join(", ")}.`);
    return { input: { backend: defaults.backend, variables }, problems };
};
exports.normalizeRemoteStorageInput = normalizeRemoteStorageInput;
const applies = (field, variables) => !field.requiredWhen ||
    variables[field.requiredWhen.variable] === field.requiredWhen.equals;
const usedSecretNames = (backend, variables) => exports.REMOTE_STORAGE_TEMPLATES[backend].secrets
    .filter((field) => applies(field, variables))
    .map((field) => field.name);
exports.usedSecretNames = usedSecretNames;
const requiredSecretNames = (backend, variables) => exports.REMOTE_STORAGE_TEMPLATES[backend].secrets
    .filter((field) => field.requiredWhen && applies(field, variables))
    .map((field) => field.name);
exports.requiredSecretNames = requiredSecretNames;
const secretProblems = (name, value) => {
    if ("pass" === name) {
        return value.length > 512 || /[\r\n]/.test(value)
            ? ["Passwords must be at most 512 characters, on one line."]
            : [];
    }
    const pem = value.trim();
    if (pem.length > 16384 ||
        !/^-----BEGIN [A-Z0-9 ]*PRIVATE KEY-----/.test(pem) ||
        !/-----END [A-Z0-9 ]*PRIVATE KEY-----$/.test(pem)) {
        return [
            "The private key must be a PEM or OpenSSH private key, from its BEGIN line to its END line.",
        ];
    }
    if (/ENCRYPTED/.test(pem)) {
        return [
            "Keys protected by a passphrase are not supported yet. Use a key without a passphrase.",
        ];
    }
    return [];
};
exports.secretProblems = secretProblems;
const HOST_KEY_ALGORITHMS = {
    "ssh-ed25519": "ssh-ed25519",
    "ecdsa-sha2-nistp256": "ecdsa-sha2-nistp256",
    "ecdsa-sha2-nistp384": "ecdsa-sha2-nistp384",
    "ecdsa-sha2-nistp521": "ecdsa-sha2-nistp521",
    "ssh-rsa": "rsa-sha2-512 rsa-sha2-256 ssh-rsa",
};
const toRcloneOptions = (input) => {
    var _a;
    const v = input.variables;
    const options = {
        type: input.backend,
        host: String(v.host),
        port: String(v.port),
        user: String(v.user),
    };
    if ("sftp" === input.backend) {
        if (!input.knownHostsFile)
            throw new Error("an sftp remote needs a known hosts file");
        const algorithms = HOST_KEY_ALGORITHMS[(_a = input.hostKeyType) !== null && _a !== void 0 ? _a : ""];
        if (!algorithms)
            throw new Error("an sftp remote needs a supported pinned host key type");
        options.shell_type = "none";
        options.disable_hashcheck = "true";
        options.known_hosts_file = input.knownHostsFile;
        options.host_key_algorithms = algorithms;
        if ("key" === v.auth) {
            if (!input.keyPem)
                throw new Error("the private key is missing");
            options.key_pem = input.keyPem.trim().replace(/\r?\n/g, "\\n");
        }
        else {
            if (!input.obscuredPass)
                throw new Error("the password is missing");
            options.pass = input.obscuredPass;
        }
    }
    else {
        if ("implicit" === v.tls)
            options.tls = "true";
        else if ("explicit" === v.tls)
            options.explicit_tls = "true";
        if (input.obscuredPass)
            options.pass = input.obscuredPass;
    }
    if (input.socksProxy)
        options.socks_proxy = input.socksProxy;
    for (const [name, value] of Object.entries(options)) {
        if (CONTROL.test(value))
            throw new Error(`the ${name} option holds a control character`);
    }
    return options;
};
exports.toRcloneOptions = toRcloneOptions;
exports.REMOTE_STORAGE_URI_PREFIX = "remote://";
const UNSAFE_PATH_CHARS = /[\u0000-\u001f\u007f\\]/;
const isSafeRemotePath = (path) => {
    if ("" === path)
        return true;
    if (path.length > 4096 ||
        path.startsWith("/") ||
        UNSAFE_PATH_CHARS.test(path))
        return false;
    const segments = path.split("/");
    return segments.every((segment, index) => (segment !== "" || index === segments.length - 1) &&
        segment !== "." &&
        segment !== "..");
};
exports.isSafeRemotePath = isSafeRemotePath;
const formatRemoteStorageUri = (credentialId, path) => {
    if (!Number.isSafeInteger(credentialId) ||
        credentialId <= 0 ||
        !(0, exports.isSafeRemotePath)(path)) {
        throw new Error("not a valid remote storage location");
    }
    return `${exports.REMOTE_STORAGE_URI_PREFIX}${credentialId}/${path}`;
};
exports.formatRemoteStorageUri = formatRemoteStorageUri;
const parseRemoteStorageUri = (value) => {
    var _a;
    if (!value.startsWith(exports.REMOTE_STORAGE_URI_PREFIX))
        return null;
    const match = /^([1-9][0-9]{0,9})(?:\/([\s\S]*))?$/.exec(value.slice(exports.REMOTE_STORAGE_URI_PREFIX.length));
    if (!match)
        return null;
    const credentialId = Number(match[1]);
    const path = (_a = match[2]) !== null && _a !== void 0 ? _a : "";
    if (!Number.isSafeInteger(credentialId) || !(0, exports.isSafeRemotePath)(path))
        return null;
    return { credentialId, path };
};
exports.parseRemoteStorageUri = parseRemoteStorageUri;
exports.REMOTE_STORAGE_REFUSAL_MESSAGES = {
    disabled: "Remote servers are not turned on for this installation. Ask an administrator.",
    login_refused: "The server refused the sign in. Check the user name and the password or key.",
    host_key_changed: "The server's host key is not the one you confirmed. If its administrator changed it, review and confirm the new key.",
    certificate_invalid: "The server's TLS certificate is not valid for this host name.",
    host_not_allowed: "This host is not allowed. Internal and reserved addresses are blocked unless an administrator allows them.",
    timeout: "The server did not answer in time.",
    not_found: "That folder was not found on the server.",
    unreachable: "The server could not be reached. Check the host name and port.",
    unavailable: "Remote servers are unavailable on this installation right now.",
    listing_too_large: "This folder holds too many entries to list. Open a folder below it.",
    sign_in_again: "This connection no longer has access. Sign in again from Connect Data Source.",
    oauth_disabled: "This sign in is not set up on this installation. Ask an administrator.",
    oauth_state_invalid: "This sign in has expired or belongs to another session. Start the sign in again.",
    oauth_consent_incomplete: "Read access to your files was not granted, so nothing can be listed. Sign in again and allow it.",
    oauth_provider_refused: "The provider did not accept the sign in. Start the sign in again.",
};
